import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Cell, LineChart, Line,
} from "recharts";
import { Loader2, Users, AlertTriangle, CheckCircle2, Gauge, Wand2, Plus } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ResourceLeveling({ projectId, onOpenTask, tasks = [], onChanged }) {
  const [data, setData] = useState(null);
  const [leveling, setLeveling] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => api.get(`/projects/${projectId}/resource-allocation`).then(({ data }) => setData(data)).catch(() => setData({ resources: [] }));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoLevel = async () => {
    setLeveling(true);
    try {
      const { data: res } = await api.post(`/projects/${projectId}/auto-level`);
      if (res.moved_count === 0) toast.info("Nothing to level — no over-allocation resolved by staggering.");
      else toast.success(`Auto-leveled: ${res.moved_count} task${res.moved_count === 1 ? "" : "s"} rescheduled.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Auto-level failed");
    } finally { setLeveling(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const taskById = Object.fromEntries(tasks.map((t) => [t.id, t]));

  if (!data.resources.length) {
    return (
      <>
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="leveling-empty">
          <Users className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">Add resources and assign them to dated tasks to see allocation and over-allocation.</p>
          <Button onClick={() => setAddOpen(true)} className="gap-1.5" data-testid="add-resource-button"><Plus className="h-4 w-4" /> Add resource</Button>
        </div>
        <div className="mt-6" />
        <QuickResourceDialog open={addOpen} onOpenChange={setAddOpen} projectId={projectId} onCreated={() => { setAddOpen(false); load(); onChanged?.(); }} />
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="resource-leveling">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Daily load vs capacity per resource. Over-allocation is flagged in red.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAddOpen(true)} className="gap-1.5" data-testid="add-resource-button"><Plus className="h-4 w-4" /> Add resource</Button>
          <Button onClick={autoLevel} disabled={leveling || !data.overallocated_count} className="gap-2" data-testid="auto-level-button">
            {leveling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Auto-level
          </Button>
        </div>
      </div>
      <QuickResourceDialog open={addOpen} onOpenChange={setAddOpen} projectId={projectId} onCreated={() => { setAddOpen(false); load(); onChanged?.(); }} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Resources</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2">{data.resources.length}</p>
        </div>
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Over-allocated</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: data.overallocated_count ? "#EF4444" : "#10B981" }}>{data.overallocated_count}</p>
        </div>
        <div className="border border-border rounded-lg p-5">
          <p className="overline text-muted-foreground">Over-allocated days</p>
          <p className="font-display text-3xl font-extrabold tracking-tight mt-2" style={{ color: data.total_overallocated_days ? "#F59E0B" : "#10B981" }}>{data.total_overallocated_days}</p>
        </div>
      </div>

      <div className="space-y-4">
        {data.resources.map((r) => (
          <div key={r.name} className="border border-border rounded-lg p-5" style={r.is_overallocated ? { borderColor: "#EF444455" } : {}} data-testid={`leveling-resource-${r.name}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-display font-bold flex items-center gap-2">
                  {r.name}
                  {r.is_overallocated
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-600 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Over-allocated</span>
                    : <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Balanced</span>}
                </p>
                <p className="text-xs text-muted-foreground">{r.role || "—"} · {r.task_count} tasks</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> capacity {r.capacity} FTE</p>
                <p className="text-sm font-bold" style={{ color: r.peak_load > r.capacity ? "#EF4444" : "#10B981" }}>peak {r.peak_load} FTE</p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={r.series} margin={{ top: 6, right: 8, bottom: 0, left: -22 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="week" tick={{ fontSize: 9 }} tickFormatter={(w) => w.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} allowDecimals />
                <Tooltip formatter={(v) => `${v} FTE`} labelFormatter={(w) => `Week of ${w}`} />
                <ReferenceLine y={r.capacity} stroke="#EF4444" strokeDasharray="4 3" label={{ value: "capacity", fontSize: 9, fill: "#EF4444", position: "right" }} />
                <Bar dataKey="load" radius={[4, 4, 0, 0]}>
                  {r.series.map((s, i) => <Cell key={i} fill={s.load > r.capacity ? "#EF4444" : "#3B82F6"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {r.is_overallocated && (
              <div className="mt-3 rounded-md bg-red-50 border border-red-100 p-3">
                <p className="text-xs font-semibold text-red-600 mb-1">Over-allocation windows</p>
                <div className="space-y-0.5">
                  {r.windows.map((w, i) => (
                    <p key={i} className="text-[11px] text-red-700">{w.start}{w.end !== w.start ? ` → ${w.end}` : ""} · peak {w.peak} FTE</p>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Suggestion: stagger overlapping tasks — shift the lower-priority task to start after the other finishes, or split the effort across more days.</p>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-1.5">
              {r.tasks.map((t) => (
                <button
                  key={t.id}
                  onClick={() => onOpenTask?.(taskById[t.id])}
                  className="text-[11px] px-2 py-1 rounded border border-border hover:border-foreground/30 transition-colors"
                  data-testid={`leveling-task-${t.id}`}
                  title={`${t.start} → ${t.end} · ${t.frac} FTE/day`}
                >
                  {t.title} <span className="text-muted-foreground">({t.frac} FTE)</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickResourceDialog({ open, onOpenChange, projectId, onCreated }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [rate, setRate] = useState("");
  const [capacity, setCapacity] = useState("1");
  const [organization, setOrganization] = useState("");
  const [influence, setInfluence] = useState("none");
  const [reportsTo, setReportsTo] = useState("none");
  const [otherManager, setOtherManager] = useState("");
  const [registry, setRegistry] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/resource-registry").then(({ data }) => setRegistry(data)).catch(() => {});
  }, [open]);

  const save = async () => {
    if (!name.trim()) return toast.error("Resource needs a name");
    if (reportsTo === "__other__" && !otherManager.trim()) return toast.error("Enter the manager's name");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/resources`, {
        name: name.trim(), role: role.trim(), email: email.trim(),
        daily_rate: Number(rate) || 0, capacity: Number(capacity) || 1,
      });
      const existing = registry.find((r) => r.name.trim().toLowerCase() === name.trim().toLowerCase());
      const rt = reportsTo === "none" ? (existing?.reports_to || "") : reportsTo === "__other__" ? otherManager.trim() : reportsTo;
      await api.put("/resource-registry", {
        name: name.trim(), role: role.trim() || existing?.role || "", email: email.trim() || existing?.email || "",
        phone: existing?.phone || "",
        organization: organization.trim() || existing?.organization || "",
        influence: influence === "none" ? (existing?.influence || "") : influence,
        reports_to: rt,
      });
      toast.success(`Resource "${name.trim()}" added`);
      setName(""); setRole(""); setEmail(""); setRate(""); setCapacity("1"); setOrganization(""); setInfluence("none"); setReportsTo("none"); setOtherManager("");
      onCreated();
    } catch (e) {
      toast.error("Could not add the resource");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quick-resource-dialog">
        <DialogHeader><DialogTitle className="font-display">Add resource</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dana Kim" data-testid="resource-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Developer" data-testid="resource-role-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Email (used for Cards & minutes)</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dana@company.com" data-testid="resource-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Daily rate</Label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0" data-testid="resource-rate-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Capacity (FTE)</Label>
              <Input type="number" step="0.1" value={capacity} onChange={(e) => setCapacity(e.target.value)} data-testid="resource-capacity-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Organization</Label>
            <Input value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="e.g. Acme Corp — Product Group" data-testid="resource-org-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Level of influence</Label>
              <Select value={influence} onValueChange={setInfluence}>
                <SelectTrigger data-testid="resource-influence-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not set —</SelectItem>
                  <SelectItem value="decision_maker">Decision maker</SelectItem>
                  <SelectItem value="advisor">Advisor / Expert</SelectItem>
                  <SelectItem value="advocate">Advocate / Adopter</SelectItem>
                  <SelectItem value="observer">Observer / Monitor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Reports to (org chart)</Label>
              <Select value={reportsTo} onValueChange={setReportsTo}>
                <SelectTrigger data-testid="resource-reportsto-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No one / top level —</SelectItem>
                  {registry.filter((r) => r.name.trim().toLowerCase() !== name.trim().toLowerCase()).map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
                  <SelectItem value="__other__">Other — add a manager…</SelectItem>
                </SelectContent>
              </Select>
              {reportsTo === "__other__" && (
                <Input value={otherManager} onChange={(e) => setOtherManager(e.target.value)} placeholder="Manager's name" data-testid="resource-othermanager-input" />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="resource-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Add resource</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
